/**
 * Analytics Service
 *
 * Comprehensive telemetry combining Google Analytics 4 and custom tracking.
 * Provides event tracking, user analytics, and conversion measurement.
 *
 * Features:
 * - Google Analytics 4 integration
 * - Custom event tracking with batching
 * - User journey tracking
 * - Performance metrics
 * - Error tracking
 * - A/B testing support
 */

import { logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { analytics } from '../config/firebase';

// =============================================================================
// TYPES
// =============================================================================

export interface TrackingEvent {
  name: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  custom?: Record<string, string | number | boolean>;
}

export interface UserProperties {
  userId?: string;
  email?: string;
  plan?: 'free' | 'pro' | 'enterprise';
  mfaEnabled?: boolean;
  signupMethod?: string;
  accountAge?: number;
}

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 's' | 'bytes' | 'count';
  tags?: Record<string, string>;
}

// =============================================================================
// EVENT DEFINITIONS
// =============================================================================

/**
 * Predefined events for consistent tracking across the app.
 * Using snake_case to match GA4 conventions.
 */
export const AnalyticsEvents = {
  // Authentication Events
  AUTH_SIGNUP_START: 'auth_signup_start',
  AUTH_SIGNUP_COMPLETE: 'auth_signup_complete',
  AUTH_SIGNUP_ERROR: 'auth_signup_error',
  AUTH_LOGIN_START: 'auth_login_start',
  AUTH_LOGIN_COMPLETE: 'auth_login_complete',
  AUTH_LOGIN_ERROR: 'auth_login_error',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_PASSWORD_RESET_REQUEST: 'auth_password_reset_request',
  AUTH_OAUTH_START: 'auth_oauth_start',
  AUTH_OAUTH_COMPLETE: 'auth_oauth_complete',
  AUTH_OAUTH_ERROR: 'auth_oauth_error',

  // MFA Events
  MFA_ENROLLMENT_START: 'mfa_enrollment_start',
  MFA_ENROLLMENT_COMPLETE: 'mfa_enrollment_complete',
  MFA_ENROLLMENT_SKIP: 'mfa_enrollment_skip',
  MFA_VERIFICATION_START: 'mfa_verification_start',
  MFA_VERIFICATION_SUCCESS: 'mfa_verification_success',
  MFA_VERIFICATION_FAILURE: 'mfa_verification_failure',
  MFA_DISABLED: 'mfa_disabled',

  // Blog Generation Events
  BLOG_GENERATION_START: 'blog_generation_start',
  BLOG_GENERATION_COMPLETE: 'blog_generation_complete',
  BLOG_GENERATION_ERROR: 'blog_generation_error',
  BLOG_GENERATION_CANCEL: 'blog_generation_cancel',
  BLOG_PREVIEW: 'blog_preview',
  BLOG_EDIT: 'blog_edit',
  BLOG_PUBLISH: 'blog_publish',
  BLOG_DOWNLOAD: 'blog_download',

  // Platform Events
  PLATFORM_CONNECT_START: 'platform_connect_start',
  PLATFORM_CONNECT_SUCCESS: 'platform_connect_success',
  PLATFORM_CONNECT_ERROR: 'platform_connect_error',
  PLATFORM_DISCONNECT: 'platform_disconnect',
  PLATFORM_PUBLISH: 'platform_publish',

  // UI Interaction Events
  PAGE_VIEW: 'page_view',
  CTA_CLICK: 'cta_click',
  FEATURE_CLICK: 'feature_click',
  PRICING_VIEW: 'pricing_view',
  PRICING_SELECT: 'pricing_select',
  FORM_SUBMIT: 'form_submit',
  FORM_ERROR: 'form_error',
  MODAL_OPEN: 'modal_open',
  MODAL_CLOSE: 'modal_close',

  // Conversion Events
  TRIAL_START: 'trial_start',
  SUBSCRIPTION_START: 'subscription_start',
  SUBSCRIPTION_UPGRADE: 'subscription_upgrade',
  SUBSCRIPTION_CANCEL: 'subscription_cancel',

  // Error Events
  ERROR_BOUNDARY: 'error_boundary',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',

  // Performance Events
  PERFORMANCE_METRIC: 'performance_metric',
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// =============================================================================
// ANALYTICS SERVICE
// =============================================================================

class AnalyticsService {
  private eventQueue: TrackingEvent[] = [];
  private flushInterval: number | null = null;
  private userId: string | null = null;

  // API endpoint for custom telemetry
  private readonly API_URL = import.meta.env.VITE_API_URL || '';

  constructor() {
    // Start event batching
    this.startBatching();
  }

  /**
   * Initialize analytics with user context
   */
  initialize(userId?: string) {
    if (userId) {
      this.setUser(userId);
    }

    // Track session start
    this.trackEvent({
      name: 'session_start',
      category: 'engagement',
      action: 'session_start',
    });
  }

  /**
   * Set the current user for tracking
   */
  setUser(userId: string, properties?: UserProperties) {
    this.userId = userId;

    // Set user in Google Analytics
    if (analytics) {
      setUserId(analytics, userId);

      if (properties) {
        setUserProperties(analytics, {
          plan: properties.plan || 'free',
          mfa_enabled: properties.mfaEnabled?.toString() || 'false',
          signup_method: properties.signupMethod || 'email',
        });
      }
    }

    // Send to custom backend
    this.sendToBackend('user_identify', { userId, ...properties });
  }

  /**
   * Clear user identity (on logout)
   */
  clearUser() {
    this.userId = null;
    // GA doesn't have a clear user ID method, but we can track the logout
    this.trackEvent({
      name: AnalyticsEvents.AUTH_LOGOUT,
      category: 'auth',
      action: 'logout',
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(event: TrackingEvent) {
    // Add to queue for batching
    this.eventQueue.push({
      ...event,
      custom: {
        ...event.custom,
        timestamp: Date.now(),
        user_id: this.userId || 'anonymous',
      },
    });

    // Send to Google Analytics immediately
    if (analytics) {
      logEvent(analytics, event.name, {
        event_category: event.category,
        event_action: event.action,
        event_label: event.label,
        value: event.value,
        ...event.custom,
      });
    }

    // Log in development
    if (import.meta.env.DEV) {
      console.log('[Analytics]', event.name, event);
    }
  }

  /**
   * Track page view
   */
  trackPageView(pagePath: string, pageTitle?: string) {
    this.trackEvent({
      name: AnalyticsEvents.PAGE_VIEW,
      category: 'navigation',
      action: 'page_view',
      label: pagePath,
      custom: {
        page_path: pagePath,
        page_title: pageTitle || document.title,
        referrer: document.referrer,
      },
    });
  }

  /**
   * Track performance metric
   */
  trackPerformance(metric: PerformanceMetric) {
    this.trackEvent({
      name: AnalyticsEvents.PERFORMANCE_METRIC,
      category: 'performance',
      action: metric.name,
      value: metric.value,
      custom: {
        unit: metric.unit,
        ...metric.tags,
      },
    });
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint: string, duration: number, success: boolean, statusCode?: number) {
    this.trackEvent({
      name: AnalyticsEvents.API_RESPONSE_TIME,
      category: 'performance',
      action: 'api_call',
      label: endpoint,
      value: duration,
      custom: {
        success: success.toString(),
        status_code: statusCode?.toString() || 'unknown',
      },
    });
  }

  /**
   * Track errors
   */
  trackError(error: Error | string, context?: Record<string, string>) {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    this.trackEvent({
      name: AnalyticsEvents.ERROR_BOUNDARY,
      category: 'error',
      action: 'error',
      label: errorMessage,
      custom: {
        error_stack: errorStack?.slice(0, 500) || '',
        ...context,
      },
    });
  }

  // ===========================================================================
  // Authentication Tracking Helpers
  // ===========================================================================

  trackSignupStart(method: 'email' | 'google' | 'apple' | 'microsoft' | 'yahoo') {
    this.trackEvent({
      name: AnalyticsEvents.AUTH_SIGNUP_START,
      category: 'auth',
      action: 'signup_start',
      label: method,
    });
  }

  trackSignupComplete(method: 'email' | 'google' | 'apple' | 'microsoft' | 'yahoo', userId: string) {
    this.setUser(userId, { signupMethod: method });
    this.trackEvent({
      name: AnalyticsEvents.AUTH_SIGNUP_COMPLETE,
      category: 'auth',
      action: 'signup_complete',
      label: method,
    });
  }

  trackLoginStart(method: 'email' | 'google' | 'apple' | 'microsoft' | 'yahoo') {
    this.trackEvent({
      name: AnalyticsEvents.AUTH_LOGIN_START,
      category: 'auth',
      action: 'login_start',
      label: method,
    });
  }

  trackLoginComplete(method: 'email' | 'google' | 'apple' | 'microsoft' | 'yahoo', userId: string) {
    this.setUser(userId);
    this.trackEvent({
      name: AnalyticsEvents.AUTH_LOGIN_COMPLETE,
      category: 'auth',
      action: 'login_complete',
      label: method,
    });
  }

  trackAuthError(method: string, error: string) {
    this.trackEvent({
      name: AnalyticsEvents.AUTH_LOGIN_ERROR,
      category: 'auth',
      action: 'auth_error',
      label: method,
      custom: { error_message: error },
    });
  }

  // ===========================================================================
  // MFA Tracking Helpers
  // ===========================================================================

  trackMfaEnrollmentStart() {
    this.trackEvent({
      name: AnalyticsEvents.MFA_ENROLLMENT_START,
      category: 'security',
      action: 'mfa_enrollment_start',
    });
  }

  trackMfaEnrollmentComplete() {
    this.trackEvent({
      name: AnalyticsEvents.MFA_ENROLLMENT_COMPLETE,
      category: 'security',
      action: 'mfa_enrollment_complete',
    });
  }

  trackMfaEnrollmentSkip() {
    this.trackEvent({
      name: AnalyticsEvents.MFA_ENROLLMENT_SKIP,
      category: 'security',
      action: 'mfa_enrollment_skip',
    });
  }

  trackMfaVerification(success: boolean) {
    this.trackEvent({
      name: success
        ? AnalyticsEvents.MFA_VERIFICATION_SUCCESS
        : AnalyticsEvents.MFA_VERIFICATION_FAILURE,
      category: 'security',
      action: success ? 'mfa_verify_success' : 'mfa_verify_failure',
    });
  }

  trackMfaEnabled() {
    this.trackEvent({
      name: AnalyticsEvents.MFA_ENROLLMENT_COMPLETE,
      category: 'security',
      action: 'mfa_enabled',
    });
  }

  trackMfaDisabled() {
    this.trackEvent({
      name: AnalyticsEvents.MFA_DISABLED,
      category: 'security',
      action: 'mfa_disabled',
    });
  }

  // ===========================================================================
  // Blog Generation Tracking Helpers
  // ===========================================================================

  trackBlogGenerationStart(topic: string) {
    this.trackEvent({
      name: AnalyticsEvents.BLOG_GENERATION_START,
      category: 'content',
      action: 'blog_generation_start',
      label: topic.slice(0, 100),
    });
  }

  trackBlogGenerationComplete(topic: string, durationMs: number) {
    this.trackEvent({
      name: AnalyticsEvents.BLOG_GENERATION_COMPLETE,
      category: 'content',
      action: 'blog_generation_complete',
      label: topic.slice(0, 100),
      value: durationMs,
    });
  }

  trackBlogGenerationError(topic: string, error: string) {
    this.trackEvent({
      name: AnalyticsEvents.BLOG_GENERATION_ERROR,
      category: 'content',
      action: 'blog_generation_error',
      label: topic.slice(0, 100),
      custom: { error_message: error },
    });
  }

  trackBlogPublish(platform: string) {
    this.trackEvent({
      name: AnalyticsEvents.BLOG_PUBLISH,
      category: 'content',
      action: 'blog_publish',
      label: platform,
    });
  }

  // ===========================================================================
  // Conversion Tracking
  // ===========================================================================

  trackTrialStart() {
    this.trackEvent({
      name: AnalyticsEvents.TRIAL_START,
      category: 'conversion',
      action: 'trial_start',
    });
  }

  trackSubscription(plan: 'pro' | 'enterprise', action: 'start' | 'upgrade' | 'cancel') {
    const eventName =
      action === 'start'
        ? AnalyticsEvents.SUBSCRIPTION_START
        : action === 'upgrade'
          ? AnalyticsEvents.SUBSCRIPTION_UPGRADE
          : AnalyticsEvents.SUBSCRIPTION_CANCEL;

    this.trackEvent({
      name: eventName,
      category: 'conversion',
      action: `subscription_${action}`,
      label: plan,
    });
  }

  // ===========================================================================
  // CTA and UI Tracking
  // ===========================================================================

  trackCtaClick(ctaName: string, location: string) {
    this.trackEvent({
      name: AnalyticsEvents.CTA_CLICK,
      category: 'engagement',
      action: 'cta_click',
      label: ctaName,
      custom: { location },
    });
  }

  trackFeatureClick(featureName: string) {
    this.trackEvent({
      name: AnalyticsEvents.FEATURE_CLICK,
      category: 'engagement',
      action: 'feature_click',
      label: featureName,
    });
  }

  trackPricingView() {
    this.trackEvent({
      name: AnalyticsEvents.PRICING_VIEW,
      category: 'conversion',
      action: 'pricing_view',
    });
  }

  trackPricingSelect(plan: string) {
    this.trackEvent({
      name: AnalyticsEvents.PRICING_SELECT,
      category: 'conversion',
      action: 'pricing_select',
      label: plan,
    });
  }

  // ===========================================================================
  // Event Batching & Backend
  // ===========================================================================

  private startBatching() {
    // Flush events every 30 seconds
    this.flushInterval = window.setInterval(() => {
      this.flushEvents();
    }, 30000);

    // Also flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flushEvents();
    });
  }

  private async flushEvents() {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    // Send batched events to custom backend
    await this.sendToBackend('events_batch', { events });
  }

  private async sendToBackend(
    eventType: string,
    data: Record<string, unknown>
  ): Promise<void> {
    // Skip if no API URL or in development without API
    if (!this.API_URL) return;

    try {
      await fetch(`${this.API_URL}/api/v1/telemetry/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: eventType,
          data,
          timestamp: new Date().toISOString(),
          client: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });
    } catch (error) {
      // Silently fail - don't break the app for analytics
      if (import.meta.env.DEV) {
        console.warn('[Analytics] Failed to send to backend:', error);
      }
    }
  }

  /**
   * Cleanup on unmount
   */
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flushEvents();
  }
}

// Singleton instance
export const analyticsService = new AnalyticsService();

// Export for use in React components
export default analyticsService;
